/**
 * @classdesc
 * Find routes between two or more locations and optionally get driving directions. The RouteTask
 * uses ArcGIS Server network analysis services to calculate routes. Network analysis services
 * allow you to solve simple routing problems as well as complex ones that take into account
 * multiple stops, barriers, and time windows.
 *
 * To work directly with the RouteTask, the basic pattern is:
 * 1. [Create the task](#constructors-summary)
 * 2. {@link module:esri/tasks/support/RouteParameters Configure the parameters}
 * 3. [Solve the route](#solve) and then specify {@link module:esri/tasks/support/RouteResult what to do with its results} and handle any errors that may be returned.
 *
 * @todo You can use the {@link module:esri/widgets/Directions} widget to easily add routing
 * capabilities to your application.
 *
 * @since 4.19
 * @module esri/rest/route
 * @see module:esri/tasks/support/RouteParameters
 * @see module:esri/tasks/support/RouteResult
 * @see [Esri Directions and Routing Services](https://developers.arcgis.com/en/features/directions/)
 * @see [Sample - RouteTask](../sample-code/tasks-route/index.html)
 */

// FROM https://devtopia.esri.com/WebGIS/arcgis-js-api/blob/4master/esri/rest/route.ts
// 5/25/2021

// esri
import { Geometry } from "esri/geometry";
// @ts-ignore
//import esriRequest from "esri/request";
import esriRequest = require("esri/request");
// @ts-ignore
import { RequestOptions } from "esri/requestInterfaces";

// esri.core
// @ts-ignore
import { Maybe } from "esri/core/maybe";
// @ts-ignore
import { UrlObject } from "esri/core/urlUtils";

// esri.rest
// @ts-ignore
import {collectGeometries} from "esri/rest/networkService";
// @ts-ignore
import { parseUrl } from "esri/rest/utils";

// esri.tasks
// @ts-ignore
import { Assembly } from "esri/tasks/interfaces";

// esri.tasks.support
// @ts-ignore
import FeatureSet from "esri/tasks/support/FeatureSet";
// @ts-ignore
import RouteParameters from "esri/tasks/support/RouteParameters";

//--------------------------------------------------------------------------
//
//  Public Methods
//
//--------------------------------------------------------------------------

/**
 * Solves the route against the route layer with the route parameters.
 *
 * @method solve
 * @instance
 *
 * @param   {String} url - URL to the ArcGIS Server REST resource that represents a network analysis service.
 * @param   {module:esri/tasks/support/RouteParameters} params - Route parameters used as input to generate the route.
 * @param   {Object} [requestOptions] - Additional {@link module:esri/request#esriRequest options} to be used for the
 *                                      data request (will override requestOptions defined during construction).
 *
 * @return {Promise<module:esri/tasks/support/RouteResult>} When resolved, returns an instance of {@link module:esri/tasks/support/RouteResult}.
 */

export async function solve(
  supportedTravelModes, // *** added for Indoors
  url: string | UrlObject,
  params: RouteParameters,
  requestOptions?: RequestOptions
) {
  const geometries: Maybe<Geometry>[] = [];
  const assembly: Assembly = {};
  const parsedUrl = parseUrl(url);

  if (params.stops && (params.stops as FeatureSet).features) {
    collectGeometries((params.stops as FeatureSet).features, geometries, "stops.features", assembly);
  }

  if (params.pointBarriers && (params.pointBarriers as FeatureSet).features) {
    collectGeometries((params.pointBarriers as FeatureSet).features, geometries, "pointBarriers.features", assembly);
  }

  if (params.polylineBarriers && (params.polylineBarriers as FeatureSet).features) {
    collectGeometries(
      (params.polylineBarriers as FeatureSet).features,
      geometries,
      "polylineBarriers.features",
      assembly
    );
  }

  if (params.polygonBarriers && (params.polygonBarriers as FeatureSet).features) {
    collectGeometries(
      (params.polygonBarriers as FeatureSet).features,
      geometries,
      "polygonBarriers.features",
      assembly
    );
  }

  const jsonParams = params.toJSON();
  jsonParams.stops = JSON.stringify(jsonParams.stops);

  if (jsonParams.barriers && typeof jsonParams.barriers === "object") {
    jsonParams.barriers = JSON.stringify(jsonParams.barriers);
  }
  if (jsonParams.pointBarriers && typeof jsonParams.pointBarriers === "object") {
    jsonParams.barriers = JSON.stringify(jsonParams.pointBarriers);
  }
  if (jsonParams.polylineBarriers && typeof jsonParams.polylineBarriers === "object") {
    jsonParams.polylineBarriers = JSON.stringify(jsonParams.polylineBarriers);
  }
  if (jsonParams.polygonBarriers && typeof jsonParams.polygonBarriers === "object") {
    jsonParams.polygonBarriers = JSON.stringify(jsonParams.polygonBarriers);
  }

  const options: RequestOptions = {
    ...requestOptions,
    query: {
      ...parsedUrl.query,
      //...paramsHelper(params),
      ...jsonParams,
      f: "json"
    }
  };

  if (options.query.barriers && options.query.barriers.features) {
    options.query.barriers = JSON.stringify(options.query.barriers);
  }

  if (options.query.outSR && options.query.outSR.wkid) {
    options.query.outSR = "" + options.query.outSR.wkid;
  }

  // *** Indoors ******************************
  // There is an issue with the Network Service
  // simplificationTolerance=null must be passed or the request fails
  // (this happens for the Redlands data)
  if (params.travelMode && params.travelMode.toJSON) {
    let tm = params.travelMode.toJSON();
    let stm;
    if (tm && supportedTravelModes) {
      supportedTravelModes.some(mode => {
        if (tm.id === mode.itemId) stm = mode;
        return !!stm;
      });
      if (stm) {
        options.query.travelMode = JSON.stringify(stm);
      }
    }
  }

  const { path } = parsedUrl;
  const solveEndpoint = "/solve";
  const url2 = path.endsWith(solveEndpoint) ? path : path + solveEndpoint;
  return esriRequest(url2, options);
}
